import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, Star, PlayCircle, Calendar, ListChecks, MessageCircle, ShieldAlert, MonitorOff, Brain, Headphones, Lock, Download, Zap, Smartphone, Eye, BookOpen } from 'lucide-react';
import { Button } from '../ui/Button';
import { LegalAccordion } from '../ui/LegalAccordion';
const CHECKOUT_URL = 'https://pay.hotmart.com/K105564779B?checkoutMode=10';
export function SalesPage() {
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-141">
      <TransitionSection data-id="element-142" />
      <OfferSection data-id="element-143" />
      <HowItWorksSection data-id="element-144" />
      <TestimonialsSection data-id="element-145" />
      <FAQSection data-id="element-146" />
      <Footer data-id="element-147" />
    </div>;
}
function TransitionSection() {
  const painPoints = [{
    emoji: '😔',
    text: 'Te vas a la cama sintiéndote culpable porque gritaste, aunque prometiste que hoy no lo harías.'
  }, {
    emoji: '⏰',
    text: 'Las mañanas son una batalla: vestirse, desayunar, salir a tiempo... todo termina en tensión.'
  }, {
    emoji: '📱',
    text: 'Cada vez que apagas la tablet o el celular, empieza una guerra. Y a veces cedes solo para tener un momento de paz.'
  }, {
    emoji: '😤',
    text: 'Le explicas las cosas mil veces, pero parece que no escucha. Y cuando insistes, la situación escala.'
  }, {
    emoji: '💔',
    text: 'Amas profundamente a tu hijo, pero hay días en que sientes que no tienes herramientas, que nada funciona.'
  }, {
    emoji: '😩',
    text: 'Llegas al final del día agotado, sin energía, preguntándote si lo estás haciendo bien.'
  }];
  return <div className="bg-brand-dark text-white relative overflow-hidden" data-id="element-148">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0" data-id="element-149">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-brand-blue/20 rounded-full blur-3xl" data-id="element-150" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-brand-lavender/20 rounded-full blur-3xl" data-id="element-151" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-green/10 rounded-full blur-3xl" data-id="element-152" />
      </div>

      {/* Empathy Section */}
      <div className="relative z-10 px-5 pt-14 pb-10 max-w-lg mx-auto" data-id="element-153">
        <p className="text-brand-lavender font-bold uppercase tracking-widest text-disclaimer mb-4 text-center" data-id="element-154">
          Sabemos cómo te sientes
        </p>
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold mb-8 text-center" data-id="element-155">
          Si algo de esto te suena familiar, no estás solo.
        </h2>

        <div className="space-y-3 mb-10" data-id="element-156">
          {painPoints.map((point, i) => <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded-2xl flex items-start space-x-3" data-id="element-157">
              <span className="text-xl shrink-0 mt-0.5" data-id="element-158">{point.emoji}</span>
              <p className="text-gray-200 leading-relaxed text-card-body" data-id="element-159">
                {point.text}
              </p>
            </div>)}
        </div>

        <div className="text-center mb-4" data-id="element-160">
          <p className="text-offer-sub font-heading font-bold" data-id="element-161">
            Nada de esto significa que seas mal padre o mala madre.
          </p>
        </div>
        <p className="text-gray-300 text-card-body text-center leading-relaxed" data-id="element-162">
          Significa que necesitas un sistema más simple. Pasos claros. Frases
          concretas. Rutinas que funcionen incluso cuando estás cansado.
        </p>
      </div>

      {/* Divider */}
      <div className="relative z-10 flex justify-center py-4" data-id="element-163">
        <div className="w-14 h-0.5 bg-white/20 rounded-full" data-id="element-164" />
      </div>

      {/* Solution Section */}
      <div className="relative z-10 px-5 pt-6 pb-14 max-w-lg mx-auto text-center" data-id="element-165">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold mb-4 text-center" data-id="element-166">
          Tu familia no necesita más gritos. Necesita el Sistema CVR.
        </h2>
        <p className="text-offer-sub text-gray-300 mb-8 leading-relaxed" data-id="element-167">
          Cuando un padre está agotado, no necesita teoría complicada. Necesita
          saber qué hacer hoy. Qué decir en el momento difícil. Cómo repetirlo
          sin perderse.
        </p>
        <p className="text-gray-400 text-card-body mb-10 leading-relaxed" data-id="element-168">
          Por eso creamos Calma en Casa: el Sistema CVR aplicado a 21 días de
          microacciones para tu familia.
        </p>

        <div className="bg-white/10 p-6 rounded-[1.5rem] backdrop-blur-md border border-white/20 text-center shadow-2xl" data-id="element-169">
          <div className="inline-block bg-brand-lavender/20 text-brand-lavender text-disclaimer font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5" data-id="element-170">
            Presentamos
          </div>
          <h3 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold mb-2 text-center text-white" data-id="element-171">
            Calma en Casa
          </h3>
          <p className="text-offer-sub text-gray-200 font-heading font-semibold mb-2 text-center" data-id="element-172">
            Plan guiado de 21 días para padres de niños intensos
          </p>
          <p className="text-card-body text-gray-400 mb-6 text-center" data-id="element-173">
            Rutinas, rabietas, pantallas y concentración con más calma.
          </p>
          <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-174">
            <img src="https://cdn.magicpatterns.com/uploads/bH6psRrq2kxjJqu2kMbeTn/MOCKUP_COMPRIMIDO.png" alt="Calma en Casa — Plan guiado de 21 días: app móvil, guía impresa, plan de rutinas y tarjetas de apoyo para padres" className="w-full h-auto" data-id="element-175" />
          </div>
        </div>

        {/* CVR highlight box */}
        <div className="mt-8 bg-white/10 border border-white/20 p-5 rounded-2xl" data-id="element-176">
          <p className="text-white font-heading font-bold text-card-title mb-2" data-id="element-177">
            No es una app para entretener a tu hijo.
          </p>
          <p className="text-gray-300 text-card-body leading-relaxed" data-id="element-178">
            Es el Sistema CVR en un plan diario para que tú sepas exactamente
            qué hacer.
          </p>
        </div>
      </div>
    </div>;
}
function OfferSection() {
  const features = [{
    icon: <ShieldAlert size={20} data-id="element-179" />,
    title: 'Sistema CVR completo',
    desc: 'Los 3 movimientos para cualquier momento difícil: Calmar, Vincular y Redirigir.'
  }, {
    icon: <Calendar size={20} data-id="element-180" />,
    title: 'Plan guiado de 21 días',
    desc: 'Microacciones diarias de 5 a 10 minutos para crear un nuevo ritmo en casa.'
  }, {
    icon: <ListChecks size={20} data-id="element-181" />,
    title: 'Rutinas visuales imprimibles',
    desc: 'Mañana, noche, tareas y transiciones. Para que el papel sea el recordatorio, no tu voz.'
  }, {
    icon: <MessageCircle size={20} data-id="element-182" />,
    title: 'Frases exactas del Sistema CVR',
    desc: 'Qué decir antes, durante y después de rabietas, pantallas y resistencia.'
  }, {
    icon: <Eye size={20} data-id="element-183" />,
    title: 'Protocolo completo de rabietas',
    desc: 'Pasos simples para antes, durante y después del conflicto.'
  }, {
    icon: <MonitorOff size={20} data-id="element-184" />,
    title: 'Estrategias para pantallas',
    desc: 'Cómo apagar tablet, celular y TV sin declarar una guerra cada vez.'
  }, {
    icon: <Brain size={20} data-id="element-185" />,
    title: 'Concentración y tareas',
    desc: 'Métodos para dividir deberes, iniciar sin bloqueo y reducir la resistencia diaria.'
  }, {
    icon: <Headphones size={20} data-id="element-186" />,
    title: 'Capítulo para el padre agotado',
    desc: 'Herramientas de regulación para ti. Porque tu calma no depende de que tu hijo se calme primero.'
  }];
  return <div className="px-5 py-14 bg-gray-50" data-id="element-187">
      <div className="max-w-lg mx-auto" data-id="element-188">
        <div className="text-center mb-10" data-id="element-189">
          <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-4 text-center" data-id="element-190">
            Empieza hoy con Calma en Casa
          </h2>
          <p className="text-offer-sub text-gray-600 text-center" data-id="element-191">
            El Sistema CVR en un plan de 21 días. Simple, diario y posible de
            aplicar cuando la casa está en caos.
          </p>
        </div>

        {/* Mockup */}
        <div className="bg-white rounded-[1.5rem] shadow-xl border border-gray-100 p-5 mb-12 relative overflow-hidden" data-id="element-192">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-brand-blue/10 to-transparent" data-id="element-193"></div>
          <div className="relative z-10 flex flex-col items-center pt-3 pb-1" data-id="element-194">
            <div className="w-16 h-16 bg-brand-blue text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-brand-blue/30" data-id="element-195">
              <Calendar size={32} data-id="element-196" />
            </div>
            <div className="w-full space-y-3" data-id="element-197">
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between" data-id="element-198">
                <div className="flex items-center space-x-3" data-id="element-199">
                  <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green" data-id="element-200">
                    <CheckCircle2 size={16} data-id="element-201" />
                  </div>
                  <span className="font-heading font-bold text-gray-800 text-card-title" data-id="element-202">
                    Día 1: La rutina visual
                  </span>
                </div>
                <PlayCircle className="text-gray-300" size={22} data-id="element-203" />
              </div>
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between" data-id="element-204">
                <div className="flex items-center space-x-3" data-id="element-205">
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100" data-id="element-206">
                    <span className="font-bold text-sm" data-id="element-207">2</span>
                  </div>
                  <span className="font-heading font-bold text-gray-800 text-card-title" data-id="element-208">
                    Día 2: Frases de conexión
                  </span>
                </div>
                <PlayCircle className="text-brand-blue" size={22} data-id="element-209" />
              </div>
              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between opacity-60" data-id="element-210">
                <div className="flex items-center space-x-3" data-id="element-211">
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100" data-id="element-212">
                    <span className="font-bold text-sm" data-id="element-213">3</span>
                  </div>
                  <span className="font-heading font-bold text-gray-800 text-card-title" data-id="element-214">
                    Día 3: Transiciones
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h3 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-6 text-center" data-id="element-215">
          Lo que incluye el plan:
        </h3>

        <div className="space-y-3.5 mb-12" data-id="element-216">
          {features.map((f, i) => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-start space-x-4 relative overflow-hidden" data-id="element-217">
              <div className="bg-blue-50 p-3 rounded-xl text-brand-blue shrink-0 relative" data-id="element-218">
                {f.icon}
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-brand-dark text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white" data-id="element-219">
                  {i + 1}
                </div>
              </div>
              <div className="pt-0.5" data-id="element-220">
                <h4 className="font-heading font-bold text-brand-dark mb-1 text-card-title" data-id="element-221">
                  {f.title}
                </h4>
                <p className="text-card-body text-gray-600 leading-relaxed" data-id="element-222">
                  {f.desc}
                </p>
              </div>
            </div>)}
        </div>

        {/* Who this is for */}
        <div className="bg-brand-beige/30 p-6 rounded-2xl border border-brand-beige/50 mb-6" data-id="element-223">
          <h3 className="font-heading font-bold text-brand-dark text-card-title mb-3 text-center" data-id="element-224">
            ¿Para quién es esto?
          </h3>
          <p className="text-card-body text-gray-700 text-center leading-relaxed" data-id="element-225">
            Para padres de niños de 4 a 10 años que quieren más calma en casa,
            mejores rutinas, menos conflictos y apoyo práctico.
          </p>
        </div>

        {/* What this is not */}
        <div className="bg-gray-100 p-6 rounded-2xl border border-gray-200 mb-12" data-id="element-226">
          <h3 className="font-heading font-bold text-gray-800 text-card-title mb-3 text-center" data-id="element-227">
            Lo que NO es
          </h3>
          <p className="text-card-body text-gray-600 text-center leading-relaxed" data-id="element-228">
            Esto no es un diagnóstico médico y no reemplaza una evaluación
            profesional. Es una herramienta educativa y práctica de apoyo para
            padres.
          </p>
        </div>
      </div>
    </div>;
}
function HowItWorksSection() {
  const steps = ['Respondes el test y descubres el patrón principal de tu familia.', 'Descubres tu perfil CVR: qué necesita tu hijo y qué necesitas tú como adulto.', 'Cada día aplicas una microacción del Sistema CVR. Solo una.', 'Usas las frases exactas, rutinas y checklists en los momentos difíciles.', 'Repites el sistema 21 días y la calma deja de depender de tu fuerza de voluntad.'];
  return <div className="px-5 py-14 bg-white" data-id="element-229">
      <div className="max-w-lg mx-auto" data-id="element-230">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-231">
          ¿Cómo funciona?
        </h2>

        <div className="space-y-4 relative" data-id="element-232">
          <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-gray-100 rounded-full" data-id="element-233" />

          {steps.map((step, i) => <div key={i} className="relative flex items-center" data-id="element-234">
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-brand-blue text-white text-card-body font-bold shadow-md shrink-0 z-10 border-3 border-white" data-id="element-235">
                {i + 1}
              </div>
              <div className="ml-4 bg-gray-50 p-4 rounded-2xl border border-gray-100 shadow-sm flex-1" data-id="element-236">
                <p className="font-medium text-gray-700 text-card-body" data-id="element-237">
                  {step}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-10 text-center bg-blue-50 p-6 rounded-2xl border border-blue-100" data-id="element-238">
          <p className="font-heading font-bold text-brand-blue mb-4 text-card-title" data-id="element-239">
            No es otra pantalla para tu hijo. Es el Sistema CVR para los padres.
          </p>
          <Button className="w-full" onClick={() => {
          if ((window as any).fbq) (window as any).fbq('track', 'AddToCart');
          window.open(CHECKOUT_URL, '_blank', 'noopener,noreferrer');
        }} data-id="element-240">
            Quiero empezar hoy
          </Button>
        </div>
      </div>
    </div>;
}
function TestimonialsSection() {
  return <div className="px-5 py-14 bg-brand-beige/30" data-id="element-241">
      <div className="max-w-lg mx-auto" data-id="element-242">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-243">
          No eres el único padre que necesitaba un sistema, no más consejos.
        </h2>

        <div className="space-y-4 mb-10" data-id="element-244">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-245">
            <div className="flex text-yellow-400 mb-3" data-id="element-246">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-247" />)}
            </div>
            <p className="text-gray-800 font-medium italic mb-4 text-card-body" data-id="element-248">
              "Por primera vez no sentí que dependía de mi paciencia. Tenía un
              sistema para volver al plan después del caos."
            </p>
            <div className="flex items-center space-x-3" data-id="element-249">
              <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center text-brand-blue text-card-body font-bold" data-id="element-250">
                M
              </div>
              <div data-id="element-251">
                <p className="text-card-body font-bold text-gray-700" data-id="element-252">
                  María, 34 años
                </p>
                <p className="text-disclaimer text-gray-500" data-id="element-253">
                  Madre de un niño de 6 años
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-254">
            <div className="flex text-yellow-400 mb-3" data-id="element-255">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-256" />)}
            </div>
            <p className="text-gray-800 font-medium italic mb-4 text-card-body" data-id="element-257">
              "Las frases del Sistema CVR me cambiaron las mañanas. Ahora sé
              exactamente qué decir."
            </p>
            <div className="flex items-center space-x-3" data-id="element-258">
              <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green text-card-body font-bold" data-id="element-259">
                C
              </div>
              <div data-id="element-260">
                <p className="text-card-body font-bold text-gray-700" data-id="element-261">
                  Carlos, 37 años
                </p>
                <p className="text-disclaimer text-gray-500" data-id="element-262">
                  Padre de una niña de 8 años
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-263">
            <div className="flex text-yellow-400 mb-3" data-id="element-264">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-265" />)}
            </div>
            <p className="text-gray-800 font-medium italic mb-4 text-card-body" data-id="element-266">
              "Lo mejor es que no es otra pantalla para mi hijo. Es una guía
              real para mí."
            </p>
            <div className="flex items-center space-x-3" data-id="element-267">
              <div className="w-10 h-10 rounded-full bg-brand-lavender/20 flex items-center justify-center text-brand-lavender text-card-body font-bold" data-id="element-268">
                A
              </div>
              <div data-id="element-269">
                <p className="text-card-body font-bold text-gray-700" data-id="element-270">
                  Andrea, 31 años
                </p>
                <p className="text-disclaimer text-gray-500" data-id="element-271">
                  Madre de dos niños
                </p>
              </div>
            </div>
          </div>
        </div>

        <Button variant="success" className="w-full" onClick={() => {
        if ((window as any).fbq) (window as any).fbq('track', 'AddToCart');
        window.open(CHECKOUT_URL, '_blank', 'noopener,noreferrer');
      }} data-id="element-272">
          Empezar Calma en Casa
        </Button>
      </div>
    </div>;
}
function FAQSection() {
  const faqs = [{
    q: '¿Qué es el Sistema CVR?',
    a: 'Es el método central de Calma en Casa. CVR significa Calmar, Vincular y Redirigir. Son tres movimientos simples que puedes aplicar en cualquier momento difícil con tu hijo, sin importar si estás cansado, si hay caos o si ya gritaste. El plan de 21 días te enseña a usarlo en tu casa, con tu hijo, en situaciones reales.'
  }, {
    q: '¿Esto es un diagnóstico de TDAH?',
    a: 'No. Este test no diagnostica TDAH ni reemplaza una evaluación médica, psicológica o educativa. Es una orientación práctica basada en el Sistema CVR de VitalyForms para padres que buscan mejorar rutinas, calma y convivencia en casa.'
  }, {
    q: '¿Mi hijo tiene que usar la pantalla?',
    a: 'No. Calma en Casa está pensado para el padre o la madre. No es otra pantalla para tu hijo.'
  }, {
    q: '¿Cuánto tiempo necesito al día?',
    a: 'El plan está diseñado con microacciones de 5 a 10 minutos al día. No necesitas ser experto ni tener tiempo libre. Solo volver al plan.'
  }, {
    q: '¿Dónde recibo el material?',
    a: 'Después de la compra recibes acceso digital con tu correo para entrar y revisar el material cuando quieras.'
  }, {
    q: '¿Sirve si mi hijo no tiene diagnóstico?',
    a: 'Sí. El plan está enfocado en desafíos cotidianos como rabietas, rutinas difíciles, pantallas y concentración. No necesitas un diagnóstico para aplicar herramientas prácticas en casa.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <div className="px-5 py-14 bg-white" data-id="element-273">
      <div className="max-w-lg mx-auto" data-id="element-274">
        <h2 className="text-offer-h1 md:text-offer-h1-desktop font-heading font-bold text-brand-dark mb-8 text-center" data-id="element-275">
          Preguntas Frecuentes
        </h2>

        <div className="space-y-3 mb-12" data-id="element-276">
          {faqs.map((faq, i) => <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden" data-id="element-277">
              <button className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-278">
                <span className="font-heading font-bold text-brand-dark pr-3 text-card-title" data-id="element-279">
                  {faq.q}
                </span>
                <ChevronDown className={`shrink-0 text-gray-400 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} size={20} data-id="element-280" />
              </button>
              <AnimatePresence data-id="element-281">
                {openIndex === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-282">
                    <div className="p-4 bg-white text-gray-600 leading-relaxed border-t border-gray-100 text-card-body" data-id="element-283">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        {/* Pricing Card */}
        <div className="bg-brand-dark text-white p-6 sm:p-10 rounded-[2rem] text-center shadow-2xl relative overflow-hidden mt-8" data-id="element-284">
          <div className="absolute top-5 -right-10 bg-brand-green text-white text-disclaimer font-bold py-1.5 px-12 transform rotate-45 shadow-md" data-id="element-285">
            -50% HOY
          </div>

          <p className="text-brand-lavender font-bold uppercase tracking-widest text-disclaimer mb-2" data-id="element-286">
            Acceso Completo
          </p>
          <p className="text-brand-green font-bold text-card-body mb-3 animate-pulse" data-id="element-287">
            🔥 Solo por hoy — 50% de descuento
          </p>
          <div className="flex justify-center items-baseline space-x-3 mb-2" data-id="element-288">
            <span className="text-2xl text-gray-500 line-through font-heading font-medium" data-id="element-289">
              $14
            </span>
            <span className="text-5xl sm:text-6xl font-heading font-bold text-white" data-id="element-290">
              $7
            </span>
          </div>
          <p className="text-disclaimer text-gray-400 mb-6" data-id="element-291">
            Precio especial por tiempo limitado
          </p>

          <div className="flex flex-col space-y-3 mb-8 text-card-body text-gray-300" data-id="element-292">
            <div className="flex items-center justify-center space-x-2.5" data-id="element-293">
              <Lock size={18} className="text-brand-green" data-id="element-294" />
              <span data-id="element-295">
                Pago seguro. Acceso inmediato al material digital completo.
              </span>
            </div>
          </div>

          <Button variant="success" className="w-full shadow-xl shadow-green-900/50" onClick={() => {
          if ((window as any).fbq) (window as any).fbq('track', 'AddToCart');
          window.open(CHECKOUT_URL, '_blank', 'noopener,noreferrer');
        }} data-id="element-296">
            Quiero mi plan guiado
          </Button>
        </div>
      </div>
    </div>;
}
function Footer() {
  return <footer className="bg-brand-dark text-gray-400 py-12 px-5" data-id="element-297">
      <div className="max-w-lg mx-auto" data-id="element-298">
        <h3 className="text-white font-heading font-bold text-brand mb-6 text-center" data-id="element-299">
          Calma en Casa — VitalyForms
        </h3>

        <div className="mb-8" data-id="element-300">
          <LegalAccordion variant="dark" data-id="element-301" />
        </div>

        <div className="bg-white/5 p-4 rounded-2xl text-disclaimer leading-relaxed text-gray-400 border border-white/10 mb-6" data-id="element-302">
          <p className="font-bold text-gray-300 mb-1.5 uppercase tracking-wider text-[11px]" data-id="element-303">
            Aviso importante:
          </p>
          <p data-id="element-304">
            Este contenido es educativo y no sustituye atención médica,
            psicológica, terapéutica o educativa profesional. El Sistema CVR es
            una herramienta práctica para mejorar la convivencia y las rutinas
            del hogar.
          </p>
        </div>

        <p className="text-center text-disclaimer text-gray-500" data-id="element-305">
          © VitalyForms. Todos los derechos reservados.
        </p>
      </div>
    </footer>;
}